import React, { useEffect, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL_CC;

const Dashboard = () => {
  // Loading and User states
  const [isLoading, setIsLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState(["User"]);
  const [activeDashboard, setActiveDashboard] = useState("reconciliation");

  // Summary data
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  // Reconciliation data
  const [sourceExtractionData, setSourceExtractionData] = useState(null);
  const [extractionTransformationData, setExtractionTransformationData] = useState(null);
  const [transformationTargetData, setTransformationTargetData] = useState(null);

  const [reconciliationLoading, setReconciliationLoading] = useState(true);
  const [reconciliationError, setReconciliationError] = useState(null);

  // Fetch user info
  useEffect(() => {
    const t = setTimeout(() => {
      setIsLoading(false);
    }, 600);

    try {
      const keysRaw = sessionStorage.getItem("msal.account.keys");
      if (keysRaw) {
        const keys = JSON.parse(keysRaw);

        if (keys?.length > 0) {
          const accountRaw = sessionStorage.getItem(keys[0]);
          if (accountRaw) {
            const account = JSON.parse(accountRaw);
            if (account?.name) {
              setLoggedInUser(account.name.split(" "));
            }
          }
        }
      }
    } catch (err) {
      console.warn("Could not parse user from sessionStorage:", err);
    }

    return () => clearTimeout(t);
  }, []);

  // Fetch Summary Data
  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/summary`);
        if (!response.ok) throw new Error("Failed to fetch summary");
        const data = await response.json();
        setSummaryData(data);
      } catch (err) {
        setSummaryError(err.message);
        console.error("Summary fetch error:", err);
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // Fetch Reconciliation Data
  useEffect(() => {
    const fetchReconciliationData = async () => {
      setReconciliationLoading(true);
      setReconciliationError(null);
      try {

        const [sourceExt, extTrans, tranTarget] = await Promise.all([
          fetch(`${API_BASE_URL}/source-extraction`).then(r => r.ok ? r.json() : Promise.reject(new Error("Failed"))).catch(() => ({ data: [] })),
          fetch(`${API_BASE_URL}/extraction-transformation`).then(r => r.ok ? r.json() : Promise.reject(new Error("Failed"))).catch(() => ({ data: [] })),
          fetch(`${API_BASE_URL}/transformation-target`).then(r => r.ok ? r.json() : Promise.reject(new Error("Failed"))).catch(() => ({ data: [] })),
        ]);

        setSourceExtractionData(sourceExt);
        setExtractionTransformationData(extTrans);
        setTransformationTargetData(tranTarget);
      } catch (err) {
        setReconciliationError(err.message);
        console.error("Reconciliation fetch error:", err);
      } finally {
        setReconciliationLoading(false);
      }
    };

    if (activeDashboard === "reconciliation") {
      fetchReconciliationData();
    }
  }, [activeDashboard]);

  const firstName = Array.isArray(loggedInUser) && loggedInUser.length > 0 ? loggedInUser[0] : "User";

  const lastName = Array.isArray(loggedInUser) && loggedInUser.length > 1 ? loggedInUser.at(-1) : "";

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-96">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-900 rounded-full animate-spin"></div>
        </div>
        <p className="mt-3 text-sm text-gray-600">Loading Dashboard...</p>
      </div>
    );
  }

  const renderKpiCards = () => {
    if (summaryLoading) {
      return (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-900 rounded-full animate-spin"></div>
        </div>
      );
    }
    if (!summaryData) {
      return null;
    }
    return (
      <div className="dashboard-cards mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white shadow-lg rounded-xl p-6 border-l-4 border-blue-600">
            <h2 className="text-lg font-bold text-gray-700 mb-2">Tables</h2>
            <p className="text-3xl font-bold mt-3" style={{ color: "rgb(0, 51, 102)" }}>
              {summaryData.total_tables}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-base font-semibold text-green-600">100%</span>
              <span className="text-sm text-gray-600">Total Tables</span>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-xl p-6 border-l-4 border-orange-500">
            <h2 className="text-lg font-bold text-gray-700 mb-2">Extraction</h2>
            <p className="text-3xl font-bold mt-3" style={{ color: "rgb(252, 135, 22)" }}>
              {summaryData.extraction_count}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-base font-semibold text-blue-600">{summaryData.extraction_progress}%</span>
              <span className="text-sm text-gray-600">Extraction Progress</span>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-xl p-6 border-l-4 border-yellow-500">
            <h2 className="text-lg font-bold text-gray-700 mb-2">Transformation</h2>
            <p className="text-3xl font-bold mt-3" style={{ color: "rgb(252, 203, 2)" }}>
              {summaryData.transformation_count}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-base font-semibold text-orange-600">{summaryData.transformation_progress}%</span>
              <span className="text-sm text-gray-600">Transformation Progress</span>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-xl p-6 border-l-4 border-green-600">
            <h2 className="text-lg font-bold text-gray-700 mb-2">Load (Target)</h2>
            <p className="text-3xl font-bold mt-3" style={{ color: "rgb(141, 190, 70)" }}>
              {summaryData.target_count}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-base font-semibold text-red-600">{summaryData.target_progress}%</span>
              <span className="text-sm text-gray-600">Target Progress</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen p-4 md:p-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-800 via-blue-500 to-orange-500 rounded-3xl p-10 text-white max-w-full mb-4">
        <h1 className="text-4xl font-bold mb-6 w-full">
          Welcome to Code Cheetah, <br />
          {firstName}
          {lastName && " " + lastName}!
        </h1>
        <p className="text-xl leading-relaxed">
          With Code Cheetah, enterprises gain automated, consistent, and resilient pipelines
          <br />
          Turning business rules into reliable Script that runs flawlessly, every time.
        </p>
      </div>

      <div className="tab-content">
        {/* Dashboard Switcher */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <button
              onClick={() => setActiveDashboard("reconciliation")}
              className={`px-6 py-3 text-base font-semibold rounded-lg transition-colors duration-150 ${activeDashboard === "reconciliation"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              Reconciliation Dashboard
            </button>

            <button
              onClick={() => setActiveDashboard("development")}
              className={`px-6 py-3 text-base font-semibold rounded-lg transition-colors duration-150 ${activeDashboard === "development"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              Development Progress
            </button>
          </div>
        </div>

        {activeDashboard === "reconciliation" && (
          <>
            {/* Summary Error */}
            {summaryError && (
              <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
                Error loading summary: {summaryError}
              </div>
            )}

            {renderKpiCards()}

            {/* Reconciliation Tables */}
            {reconciliationError && (
              <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
                Error loading reconciliation data: {reconciliationError}
              </div>
            )}

            {reconciliationLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-900 rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="space-y-10">
                {/* Source -> Extraction */}
                {sourceExtractionData?.data && (
                  <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
                    <div className="px-8 py-6 border-b border-gray-200 bg-white">
                      <h3 className="text-xl font-bold text-blue-900">Reconciliation - Source to Extraction</h3>
                    </div>
                    <table className="min-w-full text-base text-gray-700">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Table Name</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Source Count</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Extraction Count</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Difference</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Status</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>

                        {sourceExtractionData?.data?.map((row) => (
                          <tr key={row.table_name} className="border-b hover:bg-gray-50">
                            <td className="px-8 py-4 font-semibold">{row.table_name}</td>
                            <td className="px-8 py-4 text-base">{row.source_count.toLocaleString()}</td>
                            <td className="px-8 py-4 text-base">{row.extraction_count.toLocaleString()}</td>
                            <td className={`px-8 py-4 font-semibold ${row.difference === 0 ? "text-green-600" : "text-red-600"}`}>
                              {row.difference}
                            </td>
                            <td className="px-8 py-4">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${row.status === "Matched"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                                }`}>
                                {row.status}
                              </span>
                            </td>
                            <td className="px-8 py-4 text-base">{row.remarks || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Extraction -> Transformation */}
                {extractionTransformationData?.data && (
                  <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
                    <div className="px-8 py-6 border-b border-gray-200 bg-white">
                      <h3 className="text-xl font-bold text-blue-900">Reconciliation - Extraction to Transformation</h3>
                    </div>
                    <table className="min-w-full text-base text-gray-700">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Table Name</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Extraction Count</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Transformation Count</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Difference</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Status</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>

                        {extractionTransformationData?.data?.map((row) => (
                          <tr key={row.table_name} className="border-b hover:bg-gray-50">
                            <td className="px-8 py-4 font-semibold">{row.table_name}</td>
                            <td className="px-8 py-4 text-base">{row.extraction_count.toLocaleString()}</td>
                            <td className="px-8 py-4 text-base">{row.transformation_count.toLocaleString()}</td>
                            <td className={`px-8 py-4 font-semibold ${row.difference === 0 ? "text-green-600" : "text-red-600"}`}>
                              {row.difference}
                            </td>
                            <td className="px-8 py-4">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${row.status === "Matched"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                                }`}>
                                {row.status}
                              </span>
                            </td>
                            <td className="px-8 py-4 text-base">{row.remarks || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Transformation -> Target */}
                {transformationTargetData?.data && (
                  <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
                    <div className="px-8 py-6 border-b border-gray-200 bg-white">
                      <h3 className="text-xl font-bold text-blue-900">Reconciliation - Transformation to Target</h3>
                    </div>
                    <table className="min-w-full text-base text-gray-700">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Table Name</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Transformed Count</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Load Count</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Difference</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Status</th>
                          <th className="px-8 py-4 text-left font-bold text-blue-900">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>

                        {transformationTargetData?.data?.map((row) => (
                          <tr key={row.table_name} className="border-b hover:bg-gray-50">
                            <td className="px-8 py-4 font-semibold">{row.table_name}</td>
                            <td className="px-8 py-4 text-base">{row.transformation_count.toLocaleString()}</td>
                            <td className="px-8 py-4 text-base">{row.target_count.toLocaleString()}</td>
                            <td className={`px-8 py-4 font-semibold ${row.difference === 0 ? "text-green-600" : "text-red-600"}`}>
                              {row.difference}
                            </td>
                            <td className="px-8 py-4">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${row.status === "Matched"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                                }`}>
                                {row.status}
                              </span>
                            </td>
                            <td className="px-8 py-4 text-base">{row.remarks || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* === DEVELOPMENT DASHBOARD === */}
        {activeDashboard === "development" && (
          <div className="space-y-10">
            {/* EXTRACTION SECTION */}
            <div>
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-blue-900 mb-2">Extraction</h2>
                <div className="h-1 bg-gradient-to-r from-blue-900 to-blue-600 w-32 rounded-full"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Source Table</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {summaryData?.total_tables || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-600">100%</span>
                    <span className="text-sm text-gray-600">Total Table</span>
                  </div>
                </div>

                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Extracted Table</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {summaryData?.extraction_count || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-600">{summaryData?.extraction_progress || 0}%</span>
                    <span className="text-sm text-gray-600">Source to Extraction Progress</span>
                  </div>
                </div>

                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Yet To Extract</h3>
                  <p className="text-3xl font-bold mt-3" style={{ color: "rgb(252, 135, 22)" }}>
                    {(summaryData?.total_tables || 0) - (summaryData?.extraction_count || 0)}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-orange-600">
                      {summaryData ? 100 - summaryData.extraction_progress : 0}%
                    </span>
                    <span className="text-sm text-gray-600">Left To Extract</span>
                  </div>
                </div>
              </div>
            </div>

            {/* TRANSFORMATION SECTION */}
            <div>
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-blue-900 mb-2">Transformation</h2>
                <div className="h-1 bg-gradient-to-r from-blue-900 to-blue-600 w-32 rounded-full"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Target Table</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {summaryData?.total_tables || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-600">100%</span>
                    <span className="text-sm text-gray-600">Total Table</span>
                  </div>
                </div>

                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Transformed Table</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {summaryData?.transformation_count || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-600">{summaryData?.transformation_progress || 0}%</span>
                    <span className="text-sm text-gray-600">Extraction To Transformation Progress</span>
                  </div>
                </div>

                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Yet To Transform</h3>
                  <p className="text-3xl font-bold mt-3" style={{ color: "rgb(252, 135, 22)" }}>
                    {(summaryData?.total_tables || 0) - (summaryData?.transformation_count || 0)}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-orange-600">
                      {summaryData ? 100 - summaryData.transformation_progress : 0}%
                    </span>
                    <span className="text-sm text-gray-600">Left To Transform</span>
                  </div>
                </div>
              </div>
            </div>

            {/* LOAD SECTION */}
            <div>
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-blue-900 mb-2">Load</h2>
                <div className="h-1 bg-gradient-to-r from-blue-900 to-blue-600 w-32 rounded-full"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Target Tables</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {summaryData?.total_tables || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-600">100%</span>
                    <span className="text-sm text-gray-600">Total Table</span>
                  </div>
                </div>

                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Loaded Tables</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {summaryData?.target_count || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-600">{summaryData?.target_progress || 0}%</span>
                    <span className="text-sm text-gray-600">Transformation to Target Progress</span>
                  </div>
                </div>

                <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 hover:border-blue-500 hover:border-2 transition-all duration-200">
                  <h3 className="text-lg font-bold text-gray-700 mb-3">Yet To Load</h3>
                  <p className="text-3xl font-bold mt-3" style={{ color: "rgb(252, 135, 22)" }}>
                    {(summaryData?.total_tables || 0) - (summaryData?.target_count || 0)}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-orange-600">
                      {summaryData ? 100 - summaryData.target_progress : 0}%
                    </span>
                    <span className="text-sm text-gray-600">Left To Load</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;